import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MenuContent } from './MenuContent';
import { getPropertieslist } from '../../API/apiService';
import { LocalizedLink } from 'src/components/common/LocalizedLink';

type PropertyData = {
  area_id: number;
  area_name: string;
  area_name_en: string;
  property_id: number;
  chillnn_id: string;
  property_name: string;
  property_name_en: string;
};

export const MenuModal: React.FC = () => {
  const [isOverImage, setIsOverImage] = useState(false);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [isMonthly, setIsMonthly] = useState(false);
  const [isHotel, setIsHotel] = useState(false);
  const [isMagazine, setIsMagazine] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'en';
  const enPath = location.pathname.replace(/^\/(en|ja)/, '/en');
  const jaPath = location.pathname.replace(/^\/(en|ja)/, '/ja');

  const toggleLocations = () => setIsLocationsOpen(!isLocationsOpen);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handlePropertyLinkClick = () => {
    setIsLocationsOpen(false);
    closeMenu();
  };

  // disable body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
  }, [isMenuOpen]);

  // detect overlap with background images
  useEffect(() => {
    const images = Array.from(
      document.querySelectorAll('img[data-background-image="true"]')
    ) as HTMLImageElement[];

    const handleScroll = () => {
      if (!menuRef.current) return;
      const menuRect = menuRef.current.getBoundingClientRect();
      const over = images.some((img) => {
        const rect = img.getBoundingClientRect();
        return (
          rect.height > 0 &&
          menuRect.top <= rect.bottom &&
          menuRect.bottom >= rect.top
        );
      });
      setIsOverImage(over);
    };

    images.forEach((img) => {
      if (!img.complete) img.addEventListener('load', handleScroll);
    });
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      images.forEach((img) => {
        img.removeEventListener('load', handleScroll);
      });
    };
  }, []);

  // close locations dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        isLocationsOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setIsLocationsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isLocationsOpen]);

  // determine page and fetch properties
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    setIsMonthly(path.includes('monthly') || path.includes('inquire'));
    setIsHotel(path.includes('hotel'));
    setIsMagazine(path.includes('magazine'));

    getPropertieslist()
      .then((res: any) => {
        if (Array.isArray(res.data?.data)) {
          setProperties(res.data.data);
        }
      })
      .catch(console.error);
  }, [location]);

  // hide on apply/admin pages
  if (
    location.pathname.includes('apply') ||
    location.pathname.includes('admin')
  ) {
    return null;
  }

  const renderPropertyLinks = () =>
    properties.map((p) => {
      const name = currentLang === 'ja' ? p.property_name : p.property_name_en;
      const to = isHotel
        ? `/hotel/${p.property_id}`
        : `/monthly/${p.property_id}`;
      return (
        <li key={p.property_id}>
          <LocalizedLink
            to={to}
            className="block py-2 underline whitespace-nowrap"
            onClick={handlePropertyLinkClick}
          >
            {name}
          </LocalizedLink>
        </li>
      );
    });

  const logoLink = location.pathname.toLowerCase().includes('hotel')
    ? '/hotel'
    : location.pathname.toLowerCase().includes('magazine')
      ? '/magazine'
      : '/monthly';

  return (
    <div className="relative">
      {/* Mobile */}
      <div className="md:hidden">
        <div className="fixed top-4 left-4 z-40">
          <LocalizedLink to={logoLink} onClick={closeMenu}>
            <div
              lang="en"
              style={{
                fontSize: '15px',
                fontFamily: 'MNKY_BananaMonoLight',
              }}
              className={`leading-[17.6px] ${
                isOverImage ? 'text-white' : 'text-black'
              }`}
            >
              <div>The (Apartment)</div>
              <div>Hotel</div>
            </div>
          </LocalizedLink>
        </div>
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40">
          <div
            className={`flex justify-center items-center max-md:pl-20 ${
              isOverImage ? 'text-white' : 'text-black'
            }`}
            style={{
              fontSize: '15px',
              fontFamily: 'MNKY_BananaMonoLight',
            }}
          >
            <LocalizedLink
              to={enPath}
              lang="en"
              onClick={closeMenu}
              className={`mx-2 ${
                currentLang === 'en' ? 'underline' : 'no-underline'
              }`}
            >
              EN
            </LocalizedLink>
            <LocalizedLink
              to={jaPath}
              lang="ja"
              onClick={closeMenu}
              className={`mx-2 ${
                currentLang === 'ja' ? 'underline' : 'no-underline'
              }`}
            >
              JP
            </LocalizedLink>
          </div>
        </div>
        <div className="fixed top-4 right-4 z-40">
          <button
            lang="en"
            style={{
              fontSize: '15px',
              fontFamily: 'MNKY_BananaMonoLight',
            }}
            className={`${isOverImage ? 'text-white' : 'text-black'}`}
            onClick={toggleMenu}
          >
            Menu
          </button>
        </div>
        <div
          className={`fixed inset-0 z-50 bg-whiteYellow p-4 transform transition-transform duration-300 ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{ pointerEvents: isMenuOpen ? 'auto' : 'none' }}
        >
          <button
            lang="en"
            style={{
              fontSize: '15px',
              fontFamily: 'MNKY_BananaMonoLight',
            }}
            className="absolute top-4 right-4"
            onClick={toggleMenu}
          >
            close
          </button>
          <MenuContent closeMenu={closeMenu} />
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <div className="fixed top-4 left-0 w-full z-40" ref={menuRef}>
          <div className="flex justify-between items-center px-8">
            <div className="w-1/2 flex items-center">
              <LocalizedLink to={logoLink}>
                <div
                  lang="en"
                  style={{
                    fontSize: '15px',
                    fontFamily: 'MNKY_BananaMonoLight',
                  }}
                  className={`leading-[17.6px] ${
                    isOverImage ? 'text-white' : 'text-black'
                  }`}
                >
                  <div>The (Apartment)</div>
                  <div>Hotel</div>
                </div>
              </LocalizedLink>
            </div>
            <div className="w-1/2 flex items-center justify-between">
              {/* Language Switch */}
              <div
                className="flex items-center space-x-4 ml-5"
                style={{
                  fontSize: '15px',
                  fontFamily: 'MNKY_BananaMonoLight',
                }}
              >
                <LocalizedLink
                  to={enPath}
                  lang="en"
                  onClick={closeMenu}
                  className={`mx-2 ${
                    currentLang === 'en' ? 'underline' : 'no-underline'
                  }`}
                >
                  EN
                </LocalizedLink>
                <LocalizedLink
                  to={jaPath}
                  lang="ja"
                  onClick={closeMenu}
                  className={`mx-2 ${
                    currentLang === 'ja' ? 'underline' : 'no-underline'
                  }`}
                >
                  JP
                </LocalizedLink>
              </div>
              <div className="flex items-center space-x-8">
                <div className="relative">
                  <button
                    onClick={toggleLocations}
                    lang="en"
                    style={{
                      fontSize: '15px',
                      fontFamily: 'MNKY_BananaMonoLight',
                    }}
                    className="underline"
                  >
                    Locations {isLocationsOpen ? '-' : '+'}
                  </button>
                  {isLocationsOpen && (
                    <div className="absolute mt-2 bg-whiteYellow">
                      <ul>{renderPropertyLinks()}</ul>
                    </div>
                  )}
                </div>
                <LocalizedLink
                  to="/magazine"
                  lang="en"
                  style={{
                    fontSize: '15px',
                    fontFamily: 'MNKY_BananaMonoLight',
                  }}
                  className="underline"
                  onClick={closeMenu}
                >
                  Magazine
                </LocalizedLink>
                <LocalizedLink
                  to="/inquiry"
                  lang="en"
                  style={{
                    fontSize: '15px',
                    fontFamily: 'MNKY_BananaMonoLight',
                  }}
                  className="underline"
                  onClick={closeMenu}
                >
                  Contact
                </LocalizedLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuModal;
